import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useBrand } from "@/lib/brand";

export default function Chatbot() {
  const { brand } = useBrand();
  const initialMsg = brand === "ai"
    ? "Hey — I'm the AI Creator OS guide. Ask me anything about the 7 modules, the 3 tiers (Starter $47 / Creator $247 / Mastery $497), or which one is right for you."
    : "Hey — I'm the Edit Bae Studios guide. Ask about our services, case studies, or how to book a discovery call.";
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: initialMsg,
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await http.post("/ai/chat", {
        message: text,
        session_id: sessionId,
        context: brand,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Connection hiccup: " + (formatApiError(e.response?.data?.detail) || e.message) },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const accent = brand === "ai" ? "#00FFC2" : "#A855F7";

  return (
    <div className="fixed bottom-6 right-6 z-[60]" data-testid="chatbot-root">
      {open && (
        <div
          className="mb-3 w-[92vw] max-w-[380px] h-[520px] glass rounded-2xl overflow-hidden flex flex-col shadow-2xl"
          style={{ boxShadow: `0 30px 60px -20px ${accent}33, 0 0 0 1px rgba(255,255,255,0.08)` }}
          data-testid="chatbot-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b hairline">
            <div className="flex items-center gap-2">
              <span
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ background: accent, boxShadow: `0 0 12px ${accent}` }}
              />
              <span className="text-sm font-semibold">{brand === "ai" ? "AI Creator OS · Academy" : "Edit Bae · Studios"}</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white" data-testid="chatbot-close">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 hide-scroll">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[88%] text-sm leading-relaxed whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 ${
                  m.role === "user"
                    ? "ml-auto bg-white/10 border hairline"
                    : "mr-auto"
                }`}
                style={
                  m.role === "assistant"
                    ? { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }
                    : {}
                }
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="text-xs text-gray-400 pl-1 caret">Thinking</div>
            )}
          </div>

          <div className="p-3 border-t hairline flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about courses, services, or pricing…"
              className="flex-1 bg-white/5 border hairline rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
              data-testid="chatbot-input"
            />
            <button
              onClick={send}
              disabled={busy}
              data-testid="chatbot-send"
              className="rounded-full p-2.5"
              style={{ background: accent, color: "#04140f" }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chatbot-toggle-btn"
        className="rounded-full p-4 shadow-2xl"
        style={{
          background: accent,
          color: "#04140f",
          boxShadow: `0 10px 40px -8px ${accent}, 0 0 0 1px rgba(255,255,255,0.08)`,
        }}
        aria-label="Open chat"
      >
        <MessageSquare size={20} />
      </button>
    </div>
  );
}
